import { useEffect } from 'react';
import { useRoomContext } from '../RoomContext';

export { useRoomContext } from '../RoomContext';

export function useRoomSession() {
  const { state, client, createRoomAsHost, joinRoomAsStudent, joinExamByAccessCode, rejoinStoredSession, endRoom } =
    useRoomContext();
  return {
    connected: state.connected,
    roomId: state.roomId,
    joinCode: state.joinCode,
    role: state.role,
    hostId: state.hostId,
    participantId: state.participantId,
    stateVersion: state.state?.core.stateVersion ?? 0,
    lastError: state.lastError,
    client,
    createRoomAsHost,
    joinRoomAsStudent,
    joinExamByAccessCode,
    rejoinStoredSession,
    endRoom,
  };
}

export function useRoomParticipants() {
  const { state } = useRoomContext();
  const connected = state.participants.filter((p) => p.connected);
  const students = state.participants.filter((p) => p.role === 'student');
  return {
    participants: state.participants,
    connected,
    students,
    connectedCount: state.state?.core.telemetry.connectedCount ?? connected.length,
  };
}

export function useRoomPhase() {
  const { state, setPhase } = useRoomContext();
  const phase = state.state?.core.phase ?? 'LOBBY';
  return { phase, setPhase, isEnded: phase === 'ENDED' };
}

export function useActivityPayload<M extends import('../types/gameMode.types').GameMode = import('../types/gameMode.types').GameMode>() {
  const { state, setActivity, dispatchActivityAction, submitStudentInput } = useRoomContext();
  const gameMode = state.state?.core.activity.mode ?? null;
  const payload = (state.state?.payload ?? null) as import('../types/payloads').ActivityPayloadMap[M] | null;
  const runtime = state.state?.runtime ?? null;
  return {
    gameMode,
    payload,
    runtime,
    setActivity,
    dispatchActivityAction,
    submitStudentInput,
  };
}

/** Mantiene host/proyector/alumnos sincronizados con el estado autoritativo del servidor. */
export function useLiveStateSync(enabled = true) {
  const { state, client } = useRoomContext();
  const roomId = state.roomId;
  const connected = state.connected;
  const phase = state.state?.core.phase ?? 'LOBBY';

  useEffect(() => {
    if (!enabled || !roomId) return;

    void client.ensureRoomMembership();
  }, [enabled, roomId, connected, client]);

  useEffect(() => {
    if (!enabled || !roomId || !connected) return;

    const intervalMs = phase === 'ACTIVE' ? 2000 : 6000;
    const timer = setInterval(() => {
      void client.ensureRoomMembership().then((joined) => {
        if (joined) client.requestSync(roomId);
      });
    }, intervalMs);

    return () => clearInterval(timer);
  }, [enabled, roomId, connected, phase, client]);
}
